import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Table, Form, InputGroup, Button, Badge, Spinner, Alert, Pagination } from 'react-bootstrap';
import { FaSearch, FaFilter, FaPlus, FaEdit, FaTrash, FaSort, FaSortUp, FaSortDown, FaChartBar } from 'react-icons/fa';
import PageHeader from '../components/PageHeader';
import productService from '../services/productService';
import categoryService from '../services/categoryService';

function ProductsPage() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState('title');
  const [sortDirection, setSortDirection] = useState('asc');
  const [totalProducts, setTotalProducts] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const itemsPerPage = 10;
  const STORAGE_URL = process.env.REACT_APP_STORAGE_URL || 'http://localhost:5000/static/';

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    return `${STORAGE_URL}${imagePath}`;
  };

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        per_page: itemsPerPage,
        sort_by: sortField,
        sort_order: sortDirection,
      };

      if (searchQuery) {
        params.search = searchQuery;
      }

      if (selectedCategory) {
        params.category_id = selectedCategory;
      }

      const data = await productService.getAll(params);
      setProducts(data.products);
      setTotalProducts(data.total);
      setTotalPages(data.pages);
    } catch (err) {
      setError('Failed to load products');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, sortField, sortDirection, searchQuery, selectedCategory]);

  const fetchCategories = useCallback(async () => {
    try {
      const data = await categoryService.getAll({ paginate: 'false' });
      setCategories(Array.isArray(data) ? data : data.categories || []);
    } catch (err) {
      console.error('Failed to load categories:', err);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await productService.delete(id);
        fetchProducts();
      } catch (err) {
        setError('Failed to delete product');
        console.error(err);
      }
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1);
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return <FaSort className="text-muted" size={12} />;
    return sortDirection === 'asc' ? <FaSortUp size={12} /> : <FaSortDown size={12} />;
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <Container fluid className="px-4">
      <PageHeader
        title="Products"
        subtitle={`${totalProducts} products in catalog`}
      />

      <Row className="g-3 mb-4 align-items-center">
        <Col md={7}>
          <InputGroup>
            <InputGroup.Text className="bg-white">
              <FaSearch className="text-muted" />
            </InputGroup.Text>
            <Form.Control
              type="search"
              placeholder="Search products by title or description..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
            />
          </InputGroup>
        </Col>
        <Col md={3}>
          <InputGroup>
            <InputGroup.Text className="bg-white">
              <FaFilter className="text-muted" />
            </InputGroup.Text>
            <Form.Select
              value={selectedCategory}
              onChange={(e) => {
                setSelectedCategory(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">All Categories</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </Form.Select>
          </InputGroup>
        </Col>
        <Col md={2}>
          <Button
            variant="primary"
            className="w-100"
            onClick={() => navigate('/products/new')}
          >
            <FaPlus className="me-2" />
            Add Product
          </Button>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Card className="border-0 shadow-sm">
        <Card.Body className="p-0">
          <div className="table-responsive">
            <Table hover className="mb-0">
              <thead className="bg-light">
                <tr>
                  <th className="fw-semibold" style={{ cursor: 'pointer' }} onClick={() => handleSort('title')}>
                    {getSortIcon('title')} Product
                  </th>
                  <th className="fw-semibold" style={{ cursor: 'pointer' }} onClick={() => handleSort('category')}>
                    {getSortIcon('category')} Category
                  </th>
                  <th className="fw-semibold">Vendor</th>
                  <th className="fw-semibold" style={{ cursor: 'pointer' }} onClick={() => handleSort('price')}>
                    {getSortIcon('price')} Price
                  </th>
                  <th className="fw-semibold" style={{ cursor: 'pointer' }} onClick={() => handleSort('type')}>
                    {getSortIcon('type')} Type
                  </th>
                  <th className="fw-semibold" style={{ cursor: 'pointer' }} onClick={() => handleSort('views')}>
                    {getSortIcon('views')} Views
                  </th>
                  <th className="fw-semibold" style={{ cursor: 'pointer' }} onClick={() => handleSort('rating')}>
                    {getSortIcon('rating')} Rating
                  </th>
                  <th className="fw-semibold" style={{ cursor: 'pointer' }} onClick={() => handleSort('order_count')}>
                    {getSortIcon('order_count')} Orders
                  </th>
                  <th className="fw-semibold">Status</th>
                  <th className="fw-semibold text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.map((product) => (
                  <tr key={product.id}>
                    <td>
                      <div className="d-flex align-items-center">
                        {product.image ? (
                          <img
                            src={getImageUrl(product.image)}
                            alt={product.title}
                            className="rounded me-3"
                            width="56"
                            height="56"
                            style={{ objectFit: 'cover' }}
                          />
                        ) : (
                          <div
                            className="rounded me-3 bg-primary bg-opacity-10 d-flex align-items-center justify-content-center text-primary fw-bold"
                            style={{ width: '56px', height: '56px' }}
                          >
                            {product.title.charAt(0)}
                          </div>
                        )}
                        <div>
                          <div className="fw-semibold">{product.title}</div>
                          <small className="text-muted">{product.short_description}</small>
                        </div>
                      </div>
                    </td>
                    <td>
                      <Badge bg="secondary" pill>
                        {product.category_name}
                      </Badge>
                    </td>
                    <td>
                      {product.vendor_name ? (
                        <div className="d-flex align-items-center">
                          <small className="text-muted">{product.vendor_name}</small>
                          {product.vendor_verified && (
                            <Badge bg="success" className="ms-1" style={{ fontSize: '0.65rem' }}>✓</Badge>
                          )}
                        </div>
                      ) : (
                        <small className="text-muted">—</small>
                      )}
                    </td>
                    <td>${product.display_price}</td>
                    <td>
                      <Badge bg="primary" pill>
                        {product.has_file ? 'Digital' : 'Standard'}
                      </Badge>
                    </td>
                    <td>
                      <div className="d-flex align-items-center">
                        <span className="fw-semibold">{product.view_count || 0}</span>
                        <small className="text-muted ms-1">views</small>
                      </div>
                    </td>
                    <td>
                      <div className="d-flex align-items-center">
                        <span className="fw-semibold">{product.rating || 0}</span>
                        <small className="text-muted ms-1">stars</small>
                      </div>
                    </td>
                    <td>
                      <div className="d-flex align-items-center">
                        <span className="fw-semibold">{product.order_count || 0}</span>
                        <small className="text-muted ms-1">orders</small>
                      </div>
                    </td>
                    <td>
                      <Badge bg={product.is_published ? 'success' : 'warning'}>
                        {product.is_published ? 'Published' : 'Draft'}
                      </Badge>
                    </td>
                    <td className="text-end">
                      <Button
                        variant="link"
                        size="sm"
                        className="text-dark p-1"
                        onClick={() => navigate(`/products/edit/${product.id}`)}
                      >
                        <FaEdit />
                      </Button>
                      <Button
                        variant="link"
                        size="sm"
                        className="text-dark p-1 ms-2"
                        onClick={() => navigate(`/products/stats/${product.id}`)}
                      >
                        <FaChartBar />
                      </Button>
                      <Button
                        variant="link"
                        size="sm"
                        className="text-dark p-1 ms-2"
                        onClick={() => handleDelete(product.id)}
                      >
                        <FaTrash />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

      {totalPages > 1 && (
        <div className="d-flex justify-content-center mt-4">
          <Pagination>
            <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
            <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
            
            {[...Array(totalPages)].map((_, index) => {
              const page = index + 1;
              if (
                page === 1 ||
                page === totalPages ||
                (page >= currentPage - 1 && page <= currentPage + 1)
              ) {
                return (
                  <Pagination.Item
                    key={page}
                    active={page === currentPage}
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </Pagination.Item>
                );
              } else if (page === currentPage - 2 || page === currentPage + 2) {
                return <Pagination.Ellipsis key={page} disabled />;
              }
              return null;
            })}

            <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
            <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} />
          </Pagination>
        </div>
      )}
    </Container>
  );
}

export default ProductsPage;
